<template>
  <main class="dg">
    <!-- ============ ENCABEZADO ============ -->
    <header class="dg-head">
      <div>
        <div class="sec-title">
          <span class="bar-accent"></span>
          Dashboard
        </div>
        <div class="sec-sub">
          Vista global ONEST · indicadores derivados de encuestas ESAC e
          incidencias del portal
        </div>
      </div>
      <button class="dg-reload" :disabled="cargando" @click="cargar">
        <i class="bi bi-arrow-clockwise"></i>
        {{ cargando ? "Cargando…" : "Actualizar" }}
      </button>
    </header>

    <!-- ============ ESTADOS ============ -->
    <div v-if="cargando" class="empty">Cargando indicadores globales…</div>

    <div v-else-if="error" class="empty dg-error">
      <b>No se pudieron cargar los indicadores</b>
      <div>{{ error }}</div>
    </div>

    <template v-else-if="data">
      <!-- ============ FILTROS ============ -->
      <ExecutiveFilterBar
        :filtros="filtros"
        :anios-disponibles="data.aniosDisponibles"
        :cuentas="raw.cuentas"
        :secciones="data.categorias.secciones"
        :resultado="{
          cuentas: data.resumen.cuentasEvaluadas,
          encuestas: data.resumen.totalEjecuciones,
        }"
        @update="aplicarFiltro"
      />

      <div v-if="data.nombreCuenta" class="dg-scope">
        <i class="bi bi-funnel-fill"></i>
        Tablero acotado a <strong>{{ data.nombreCuenta }}</strong>
      </div>

      <!-- ============ TARJETAS KPI ============ -->
      <section class="dg-kpis">
        <article class="card-custom kpi-card kpi-primary">
          <div class="label-custom">Calificación global ONEST</div>
          <div class="kpi-value">{{ fmt(r.scoreGlobal) }}%</div>
          <div class="kpi-hint">
            Promedio de {{ r.totalEjecuciones }} encuestas ESAC
          </div>
        </article>

        <article class="card-custom kpi-card">
          <div class="label-custom">NPS</div>
          <div class="kpi-value">{{ fmt(r.nps, 0) }}</div>
          <div class="kpi-hint">
            {{ r.npsMuestra }} respuestas · promotores − detractores
          </div>
        </article>

        <article class="card-custom kpi-card">
          <div class="label-custom">CES</div>
          <div class="kpi-value">{{ fmt(r.ces) }}</div>
          <div class="kpi-hint">{{ r.cesMuestra }} respuestas con dato</div>
        </article>

        <article class="card-custom kpi-card">
          <div class="label-custom">CSAT</div>
          <div class="kpi-value">{{ fmt(r.csat) }}</div>
          <!-- CSAT llega nulo en buena parte de las ejecuciones: se declara la
               muestra para no leer el número como cobertura total. -->
          <div class="kpi-hint kpi-warn">
            Solo {{ r.csatMuestra }} de {{ r.totalEjecuciones }} encuestas
          </div>
        </article>

        <article class="card-custom kpi-card">
          <div class="label-custom">Cuentas evaluadas</div>
          <div class="kpi-value">
            {{ r.cuentasEvaluadas }}<span class="kpi-of">/{{ r.cuentasObjetivo }}</span>
          </div>
          <div class="kpi-hint">Cuentas activas con al menos una ESAC</div>
        </article>

        <article class="card-custom kpi-card kpi-success">
          <div class="label-custom">Cobertura</div>
          <div class="kpi-value">{{ r.cobertura }}%</div>
          <div class="kpi-hint">Evaluadas sobre cuentas activas</div>
        </article>

        <article class="card-custom kpi-card">
          <div class="label-custom">Encuestas aplicadas</div>
          <div class="kpi-value">{{ r.totalEjecuciones }}</div>
          <div class="kpi-hint">Histórico completo ESAC</div>
        </article>

        <article
          class="card-custom kpi-card"
          :class="{ 'kpi-danger': r.cuentasEnRiesgo > 0 }"
        >
          <div class="label-custom">Cuentas en riesgo</div>
          <div class="kpi-value">{{ r.cuentasEnRiesgo }}</div>
          <div class="kpi-hint">
            + {{ r.cuentasEnSeguimiento }} en seguimiento
          </div>
        </article>
      </section>

      <!-- ============ COBERTURA + SEMÁFORO ============ -->
      <section class="dg-grid-2">
        <article class="card-custom">
          <div class="dg-card-title">Cobertura de evaluación</div>
          <div class="dg-card-sub">
            Cuentas activas con encuesta ESAC aplicada
          </div>
          <div class="dg-donut-wrap">
            <div class="dg-donut">
              <DonutChart :data="data.cobertura" :size="164" :stroke="26" />
              <div class="dg-donut-center">
                <span class="dg-donut-num">{{ r.cobertura }}%</span>
                <span class="dg-donut-cap">cobertura</span>
              </div>
            </div>
            <ul class="dg-legend">
              <li v-for="s in data.cobertura" :key="s.name">
                <span class="dot" :style="{ background: s.color }"></span>
                <span class="lg-name">{{ s.name }}</span>
                <span class="lg-val">{{ s.value }}</span>
              </li>
            </ul>
          </div>
        </article>

        <article class="card-custom">
          <div class="dg-card-title">Distribución de cuentas por estado</div>
          <div class="dg-card-sub">
            Semáforo de la evaluación vigente de cada cuenta
          </div>
          <div class="dg-bars">
            <div v-for="s in data.semaforo" :key="s.key" class="dg-bar-row">
              <span class="dg-bar-name">{{ s.label }}</span>
              <div class="dg-bar-track">
                <div
                  class="dg-bar-fill"
                  :style="{ width: pct(s.value, r.cuentasEvaluadas), background: s.color }"
                ></div>
              </div>
              <span class="dg-bar-val">{{ s.value }}</span>
              <span class="dg-bar-pct">{{ s.pct }}%</span>
            </div>
          </div>
        </article>
      </section>

      <!-- ============ RESUMEN GENERAL ANUAL ============ -->
      <ResumenAnual
        v-if="data.resumenAnual.disponible"
        :serie="data.evolucion"
        :resumen="data.resumenAnual"
        :benchmark="data.benchmark"
        :nombre-cuenta="data.nombreCuenta"
      />

      <!-- ============ DESEMPEÑO POR ÁREA ============ -->
      <AreaEvolutionChart v-if="data.categorias.disponible" :cat="data.categorias" />

      <!-- ============ COMPARATIVO ============ -->
      <AccountVsOnestChart
        v-if="data.vsOnest"
        :data="data.vsOnest"
        :nombre="data.nombreCuenta"
      />

      <AccountComparisonTable
        v-if="!data.nombreCuenta"
        :filas="data.comparativo"
        :anios="data.aniosActivos"
        @ver-detalle="abrirDetalle"
      />

      <!-- ============ PANEL DE DETALLE ============ -->
      <AccountDetailDrawer
        v-if="detalleCuenta"
        :key="detalleCuenta.idCuenta"
        :cuenta="detalleCuenta"
        :ejecuciones="ejecucionesDetalle"
        :serie="serieDetalle"
        :benchmark="data.evolucion"
        :incidencias="incidenciasDetalle"
        @close="detalleCuenta = null"
      />

      <!-- ============ RANKING DE CUENTAS ============ -->
      <section class="dg-grid-2">
        <article class="card-custom">
          <div class="dg-card-title">Mejores cuentas</div>
          <div class="dg-card-sub">Score de la evaluación vigente</div>
          <RankingList :items="data.top" />
        </article>

        <article class="card-custom">
          <div class="dg-card-title">Cuentas con menor score</div>
          <div class="dg-card-sub">Prioridad de seguimiento</div>
          <RankingList :items="data.bottom" />
        </article>
      </section>

      <!-- ============ INCIDENCIAS ============ -->
      <section v-if="data.incidencias.disponible" class="dg-grid-2">
        <article class="card-custom">
          <div class="dg-card-title">Incidencias por estatus</div>
          <div class="dg-card-sub">
            {{ data.incidencias.total }} registros del portal de seguimiento
          </div>
          <div class="dg-bars">
            <div
              v-for="s in data.incidencias.porEstatus"
              :key="s.name"
              class="dg-bar-row"
            >
              <span class="dg-bar-name">{{ s.name }}</span>
              <div class="dg-bar-track">
                <div
                  class="dg-bar-fill"
                  :style="{ width: pct(s.value, data.incidencias.total), background: s.color }"
                ></div>
              </div>
              <span class="dg-bar-val">{{ s.value }}</span>
            </div>
          </div>
        </article>

        <article class="card-custom">
          <div class="dg-card-title">Incidencias por área responsable</div>
          <div class="dg-card-sub">Top 8 áreas con mayor carga</div>
          <div class="dg-bars">
            <div
              v-for="a in data.incidencias.porArea"
              :key="a.name"
              class="dg-bar-row"
            >
              <span class="dg-bar-name">{{ a.name }}</span>
              <div class="dg-bar-track">
                <div
                  class="dg-bar-fill"
                  :style="{ width: pct(a.value, maxArea), background: a.color }"
                ></div>
              </div>
              <span class="dg-bar-val">{{ a.value }}</span>
            </div>
          </div>
        </article>
      </section>

      <!-- ============ DIRECTORIO ============ -->
      <article v-if="r.totalContactos != null" class="card-custom dg-directorio">
        <div>
          <div class="dg-card-title">Directorio CX</div>
          <div class="dg-card-sub">Participación ejecutiva registrada</div>
        </div>
        <div class="dg-dir-stats">
          <div class="dg-dir-stat">
            <span class="dg-dir-num">{{ r.totalContactos }}</span>
            <span class="dg-dir-lbl">Contactos</span>
          </div>
          <div class="dg-dir-stat">
            <span class="dg-dir-num">{{ r.decisionMakers }}</span>
            <span class="dg-dir-lbl">Decision makers</span>
          </div>
          <div class="dg-dir-stat">
            <span class="dg-dir-num">{{ r.promotores }}</span>
            <span class="dg-dir-lbl">Promotores</span>
          </div>
          <div class="dg-dir-stat">
            <span class="dg-dir-num">{{ r.warnings }}</span>
            <span class="dg-dir-lbl">Warnings</span>
          </div>
        </div>
      </article>
    </template>
  </main>
</template>

<script>
import DonutChart from "./portal/DonutChart.vue";
import RankingList from "./RankingList.vue";
import ResumenAnual from "./ResumenAnual.vue";
import AreaEvolutionChart from "./AreaEvolutionChart.vue";
import ExecutiveFilterBar from "./ExecutiveFilterBar.vue";
import AccountComparisonTable from "./AccountComparisonTable.vue";
import AccountVsOnestChart from "./AccountVsOnestChart.vue";
import AccountDetailDrawer from "./AccountDetailDrawer.vue";
import dashboardService, {
  FILTROS_DEFAULT,
} from "../../services/dashboard.service";

export default {
  name: "DashboardGeneral",
  components: {
    DonutChart,
    RankingList,
    ResumenAnual,
    AreaEvolutionChart,
    ExecutiveFilterBar,
    AccountComparisonTable,
    AccountVsOnestChart,
    AccountDetailDrawer,
  },

  data() {
    return {
      raw: null,
      data: null,
      filtros: { ...FILTROS_DEFAULT },
      detalleCuenta: null,
      cargando: true,
      error: "",
    };
  },

  computed: {
    // Atajo al bloque de resumen; el template lo usa en todas las tarjetas KPI.
    r() {
      return this.data ? this.data.resumen : {};
    },

    maxArea() {
      const areas = this.data?.incidencias?.porArea || [];
      return Math.max(1, ...areas.map((a) => a.value));
    },

    // ---- Datos que alimentan el panel de detalle ----
    // Se toman de `raw` (histórico completo) y no del recorte filtrado: el
    // panel muestra la ficha íntegra de la cuenta.
    ejecucionesDetalle() {
      if (!this.detalleCuenta || !this.raw) return [];
      return this.raw.ejecuciones.filter(
        (e) => String(e.idCuenta) === String(this.detalleCuenta.idCuenta),
      );
    },

    serieDetalle() {
      if (!this.detalleCuenta) return [];
      const anios = this.data?.evolucion.map((p) => p.anio) || [];
      return anios
        .map((anio) => {
          const delAnio = this.ejecucionesDetalle.filter((e) => e.anio === anio);
          if (!delAnio.length) return null;
          const prom = (campo) => {
            const v = delAnio.map((e) => e[campo]).filter((x) => x != null);
            return v.length
              ? Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10
              : null;
          };
          const votos = delAnio.map((e) => e.nps).filter((v) => v != null);
          return {
            anio,
            score: prom("scorePct"),
            nps: votos.length
              ? Math.round(
                  ((votos.filter((v) => v >= 9).length -
                    votos.filter((v) => v <= 6).length) /
                    votos.length) *
                    100,
                )
              : null,
            ces: prom("ces"),
            csat: prom("csat"),
            encuestas: delAnio.length,
            cuentas: 1,
            cobertura: 100,
          };
        })
        .filter(Boolean);
    },

    incidenciasDetalle() {
      if (!this.detalleCuenta || !this.raw) return [];
      return this.raw.incidencias.filter(
        (i) => i.cliente === this.detalleCuenta.cliente,
      );
    },
  },

  mounted() {
    this.cargar();
  },

  methods: {
    async cargar() {
      this.cargando = true;
      this.error = "";
      try {
        this.raw = await dashboardService.cargarDatos();
        this.recalcular();
      } catch (e) {
        console.error("Error cargando el dashboard global:", e);
        this.error = e.message || "Error inesperado al consultar el servicio.";
      } finally {
        this.cargando = false;
      }
    },

    // Recalcula el workspace sin volver a pedir datos.
    recalcular() {
      if (this.raw) this.data = dashboardService.derivar(this.raw, this.filtros);
    },

    // Aplica un parche de filtros. Cambiar de cuenta requiere el desglose por
    // sección de esa cuenta, que es la única petición adicional del tablero.
    async aplicarFiltro(parche) {
      const cambioCuenta =
        "idCuenta" in parche && parche.idCuenta !== this.filtros.idCuenta;
      this.filtros = { ...this.filtros, ...parche };

      if (cambioCuenta) {
        try {
          this.raw.categoriasCuenta = this.filtros.idCuenta
            ? await dashboardService.cargarCategoriasCuenta(this.filtros.idCuenta)
            : null;
        } catch (e) {
          console.error("Error cargando categorías de la cuenta:", e);
          this.raw.categoriasCuenta = null;
        }
      }

      this.recalcular();
    },

    abrirDetalle(fila) {
      this.detalleCuenta = fila;
    },

    fmt(valor, decimales = 1) {
      if (valor == null) return "—";
      return Number(valor).toFixed(decimales);
    },

    pct(valor, total) {
      if (!total) return "0%";
      return Math.round((valor / total) * 100) + "%";
    },
  },
};
</script>

<style scoped>
.dg {
  max-width: 1400px;
  margin: 0 auto;
  padding: clamp(16px, 3vw, 32px);
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.dg-head {
  display: flex;
  background: var(--white);
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.dg-reload {
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt);
  border-radius: 12px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s ease;
}
.dg-reload:hover:not(:disabled) {
  border-color: var(--primary);
  color: var(--primary);
}
.dg-reload:disabled {
  opacity: 0.6;
  cursor: default;
}

.dg-error b {
  color: var(--rojo);
}

.dg-scope {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--brand-text);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 12px;
}

/* ---------- KPIs ---------- */
.dg-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
}

.kpi-card {
  border-radius: 16px;
  padding: 16px 18px;
}

.kpi-value {
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  line-height: 1.15;
  color: var(--heading);
  font-variant-numeric: tabular-nums;
}

.kpi-of {
  font-size: 17px;
  font-weight: 600;
  color: var(--txt-faint);
}

.kpi-hint {
  font-size: 11px;
  color: var(--txt-dim);
  margin-top: 4px;
  line-height: 1.35;
}
.kpi-warn {
  color: var(--warn);
  font-weight: 600;
}

/* Franja de acento superior por tono de la tarjeta. */
.kpi-primary::before,
.kpi-success::before,
.kpi-danger::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 3px;
}
.kpi-primary::before {
  background: var(--primary);
}
.kpi-success::before {
  background: var(--secondary);
}
.kpi-danger::before {
  background: var(--rojo);
}
.kpi-danger .kpi-value {
  color: var(--rojo);
}

/* ---------- Layout de tarjetas ---------- */
.dg-grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}

.dg-card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--heading);
}

.dg-card-sub {
  font-size: 12px;
  color: var(--txt-dim);
  margin-bottom: 14px;
  line-height: 1.4;
}

/* ---------- Dona ---------- */
.dg-donut-wrap {
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.dg-donut {
  position: relative;
  line-height: 0;
}

.dg-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
}

.dg-donut-num {
  font-size: 27px;
  font-weight: 800;
  color: var(--heading);
  font-variant-numeric: tabular-nums;
}

.dg-donut-cap {
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--txt-faint);
}

.dg-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 160px;
}
.dg-legend li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
}
.dg-legend .dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}
.dg-legend .lg-name {
  flex: 1;
  color: var(--txt-dim);
}
.dg-legend .lg-val {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* ---------- Barras ---------- */
.dg-bars {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dg-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
}

.dg-bar-name {
  width: 120px;
  flex-shrink: 0;
  color: var(--txt-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dg-bar-track {
  flex: 1;
  height: 18px;
  border-radius: 6px;
  background: var(--gray-100);
  overflow: hidden;
}

.dg-bar-fill {
  height: 100%;
  min-width: 3px;
  border-radius: 6px;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.dg-bar-val {
  width: 40px;
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.dg-bar-pct {
  width: 42px;
  text-align: right;
  color: var(--txt-faint);
  font-variant-numeric: tabular-nums;
}

/* ---------- Directorio ---------- */
.dg-directorio {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}
.dg-directorio .dg-card-sub {
  margin-bottom: 0;
}

.dg-dir-stats {
  display: flex;
  gap: clamp(18px, 4vw, 46px);
  flex-wrap: wrap;
}

.dg-dir-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dg-dir-num {
  font-size: 26px;
  font-weight: 800;
  color: var(--heading);
  font-variant-numeric: tabular-nums;
}

.dg-dir-lbl {
  font-size: 11px;
  color: var(--txt-dim);
}
</style>
